import { StatusBadge } from "@/admin/hosts/StatusBadge.tsx";
import type { AdminHostWithType } from "@/admin/hosts/hostQuery.ts";
import { adminHostQueryAtom } from "@/admin/hosts/hostQuery.ts";
import { LeftRight } from "@/components/LeftRight.tsx";
import { WMeter } from "@/components/WMeter.tsx";
import { WSelect, type WSelectStyleProps, type WSelectValueProps } from "@/components/forms/WSelect.tsx";
import { WSelectWrapper } from "@/components/forms/WSelectWrapper.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { notNull } from "@warrenio/utils/notNull";

export interface HostSelectProps extends WSelectStyleProps, WSelectValueProps<AdminHostWithType, string> {
    items: AdminHostWithType[];
    location: string;
    pool: string;
}

function HostSelectCustom({ items, ...props }: HostSelectProps) {
    const data = items.sort((a, b) => {
        return a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: "base",
        });
    });

    return (
        <WSelect
            aria-label="Select pool"
            items={data}
            getKey={(item) => item.uuid}
            getTextValue={(item) => item.name}
            searchItems
            placeholder="Any available"
            renderSelectedValue={(item) =>
                !!item && (
                    <LeftRight
                        icon={<MaskIcon className="jp-host-icon color-primary size-1rem" />}
                        title={item?.name}
                        right={<StatusBadge item={notNull(item)} />}
                    />
                )
            }
            {...props}
        >
            {(item) => (
                <LeftRight
                    icon={<MaskIcon className="jp-host-icon color-primary size-1rem" />}
                    title={
                        <div>
                            <div>{item.name}</div>
                            <div className="font-size-small color-muted">{item.ip}</div>
                        </div>
                    }
                    right={
                        <div className="HStack justify-end gap-0.5rem">
                            <div className="w-4rem">
                                <WMeter
                                    title="vCPU:"
                                    value={Number(((item.allocated_vcpu / item.usable_vcpu_amount) * 100).toFixed(0))}
                                />
                            </div>

                            <div className="w-4rem">
                                <WMeter
                                    title="vRAM:"
                                    value={Number(((item.allocated_vmem / item.usable_vmem_amount) * 100).toFixed(0))}
                                />
                            </div>
                        </div>
                    }
                />
            )}
        </WSelect>
    );
}

export interface HostSelectLoaderProps extends Omit<HostSelectProps, "items"> {}

function HostSelectLoader({ ...props }: HostSelectLoaderProps) {
    const data = useSuspenseQueryAtom(adminHostQueryAtom(props.location));
    const hosts = [...data.values()].filter((item) => item.pool_uuid === props.pool);
    return <HostSelectCustom items={hosts} defaultValueKey={props.valueKey} {...props} />;
}

export function HostSelect(props: HostSelectLoaderProps) {
    return (
        <WSelectWrapper fallback={(renderProps) => <HostSelectCustom {...props} {...renderProps} />}>
            <HostSelectLoader {...props} />
        </WSelectWrapper>
    );
}
